/**
 * Loading Google's SDK, once, on demand.
 *
 * The map and the search bar both need it and either may ask first, so the
 * promise is cached rather than the script tag counted — two components
 * mounting together must not inject two tags and race.
 *
 * ON DEMAND because only the address step needs it. Google's loader pulls
 * several hundred kilobytes; putting it in the bundle every customer fetches
 * to look at the home page would be paid for by people who never check out.
 *
 * THE KEY IS PUBLIC. It is baked into the bundle at build time and visible to
 * anyone who opens the network tab — that is how browser Maps keys work. What
 * protects it is the restriction set in Google Cloud: HTTP referrer, and only
 * the two APIs we use. Treating it as a secret here would be theatre.
 */

const CALLBACK = '__printomanGoogleMapsReady';

let loader: Promise<typeof google> | null = null;

export function googleMapsKey(): string {
  const key: unknown = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  return typeof key === 'string' ? key : '';
}

/** True when the app was built with a key. Callers fall back without one. */
export function hasGoogleMaps(): boolean {
  return googleMapsKey().length > 0;
}

export function loadGoogleMaps(): Promise<typeof google> {
  if (loader) return loader;

  loader = new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('no document'));
      return;
    }
    const key = googleMapsKey();
    if (!key) {
      reject(new Error('VITE_GOOGLE_MAPS_API_KEY is not set'));
      return;
    }
    // Already there — a hot reload, or a second mount after the tag landed.
    if (window.google?.maps) {
      resolve(window.google);
      return;
    }

    const script = document.createElement('script');
    const params = new URLSearchParams({
      key,
      // `places` for the search bar. Asked for here rather than imported
      // later because the loader cannot add a library after the fact.
      libraries: 'places',
      // Google's own guidance, and it suppresses their console warning about
      // blocking loads.
      loading: 'async',
      callback: CALLBACK,
      v: 'weekly',
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;

    const globalScope = window as unknown as Record<string, unknown>;
    globalScope[CALLBACK] = () => {
      delete globalScope[CALLBACK];
      resolve(window.google);
    };
    script.addEventListener('error', () => {
      // Reset so a later attempt can retry rather than inheriting a rejected
      // promise for the life of the page — a customer who was offline for a
      // moment should be able to try again.
      loader = null;
      reject(new Error('Google Maps failed to load'));
    });

    document.head.append(script);
  });

  return loader;
}
